import {
  ExamCenterSearchSelection,
  ProvinceSearchSelection,
} from '@exam-center/exam-location-management';
import { useGetAllProvincesQuery } from '@exam-center/graphql';
import { Flex, Form, Button, Divider } from '@shared/components';
import { zodResolver } from '@shared/utils';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useAdminExamLocationRequestListSearchParams } from '../../hooks';

import {
  type AdminExamLocationRequestListFilterFormInput,
  AdminExamLocationRequestListFilterFormSchema,
} from './admin-exam-location-request-list-filter-form-schema';

export default function AdminExamLocationRequestListFilterForm() {
  const [{ province, examCenters }, { changeProvince, changeExamCenters }] =
    useAdminExamLocationRequestListSearchParams();
  const { data } = useGetAllProvincesQuery();

  const provinces = data?.ecGetAllProvinces?.provinces ?? [];
  const defaultProvince = provinces.find((item) => item.id === province);

  const formContext = useForm<AdminExamLocationRequestListFilterFormInput>({
    resolver: zodResolver(AdminExamLocationRequestListFilterFormSchema),
    defaultValues: {
      province: defaultProvince ? { label: defaultProvince.nameTh, value: province } : undefined,
      examCenters: examCenters.map((center) => ({ label: center, value: center })),
    },
  });

  useEffect(() => {
    if (defaultProvince) {
      formContext.setValue('province', { label: defaultProvince.nameTh, value: province });
    }
  }, [province, defaultProvince, formContext]);

  const handleSubmitFilter = () => {
    const { province, examCenters } = formContext.getValues();

    changeProvince(province?.value ?? '');
    changeExamCenters(examCenters.map((center) => center.value));
  };

  const handleResetFilter = () => {
    formContext.reset({
      province: undefined,
      examCenters: [],
    });
    handleSubmitFilter();
  };

  return (
    <FormProvider {...formContext}>
      <Form>
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(4),
            p: theme.spacing(4),
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: theme.shape.rounded(2),
          })}
        >
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Flex sx={{ width: 259 }}>
              <ProvinceSearchSelection value={formContext.watch('province') ?? undefined} />
            </Flex>
            <Flex sx={{ width: 259 }}>
              <ExamCenterSearchSelection
                value={formContext.watch('examCenters').map((center) => center.value) ?? []}
              />
            </Flex>
          </Flex>
          <Divider />
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(2),
              justifyContent: 'flex-end',
            })}
          >
            <Button color='secondary' variant='outlined' type='reset' onClick={handleResetFilter}>
              Reset
            </Button>
            <Button onClick={handleSubmitFilter}>Apply</Button>
          </Flex>
        </Flex>
      </Form>
    </FormProvider>
  );
}
